import { useAppDispatch } from "../../../components/hooks";
import { ApolloError, useMutation } from "@apollo/client";
import { addPopupQueue, removePopupQueue } from "../../../store/slices/popup";
import { PopupType } from "../../../constants/popup";
import { useCallback } from "react";
import { updateOpeningInventory } from "../../../store/slices/inventories";
import { showSnackbar } from "../../../store/slices/alert";
import {
  CoinType,
  CoinWallet,
  getUserInventoriesGQL,
  InventoriesOutput,
  Inventory,
  InventoryAvatarData,
  InventoryCost,
  InventoryStatus,
} from "@onmo/graphql/graphql";
import {
  applyInventoryGQL,
  purchaseInventoryGQL,
  getListInventoriesGQL,
} from "@onmo/graphql/graphql";
import { User } from "../../../models/user";
import { useWalletConfig } from "../../useWalletConfig";

export const getInventoryCost = (
  cost?: (InventoryCost | null | undefined)[] | null,
  spendableCoinType?: CoinType,
) => {
  const type = spendableCoinType || CoinType.SpendableGzTokens;
  const spendableCost = cost?.find((c) => c?.coinType === type);

  if (!spendableCost) return undefined;
  return {
    coinType: spendableCost.coinType,
    amount: spendableCost.amount,
  };
};

const inputPurchaseInventory = (
  openingInventory: Inventory,
  spendableCoinType?: CoinType,
) => {
  return {
    input: {
      applyImmediately: openingInventory.applyImmediately,
      inventoryId: openingInventory.id,
      inventoryCost: getInventoryCost(openingInventory.prices as InventoryCost[], spendableCoinType),
    },
  };
};

export const usePurchaseInventory = (options?: { showPopup?: boolean }) => {
  const showPopup = options?.showPopup ?? true;
  const dispatch = useAppDispatch();
  const [purchaseInventoryAction] = useMutation(purchaseInventoryGQL);
  const { walletConfig } = useWalletConfig();
  const spendableType = walletConfig.primaryCurrency.spendable;

  //purchase avatar/bundle function
  const purchaseInventory = async (
    bundle: Inventory,
    isBuyBundle?: boolean,
  ) => {
    try {
      const result = await purchaseInventoryAction({
        variables: inputPurchaseInventory(bundle, spendableType),
        refetchQueries: [
          {
            query: getUserInventoriesGQL,
            variables: {
              input: {
                page: { limit: 50, offset: 0 },
                filters: {
                  status: [InventoryStatus.Available, InventoryStatus.Restricted],
                },
              },
            },
          },
        ],
        awaitRefetchQueries: true,
        update(cache) {
          //update current purchased bundle or avatar
          const inventoriesId = cache.identify({
            __typename: "Inventory",
            id: bundle.id,
          });
          if (inventoriesId) {
            cache.modify({
              id: inventoriesId,
              fields: {
                isPurchased() {
                  return true;
                },
              },
            });
          }
          if (isBuyBundle) {
            //update list purchased avatar in current bundle
            const existingAvatarByBundle = cache.readQuery<{
              inventories: InventoriesOutput;
            }>({
              query: getListInventoriesGQL,
              variables: {
                input: { filters: { bundleInventoryId: bundle.id } },
              },
            });
            if (existingAvatarByBundle?.inventories?.items) {
              existingAvatarByBundle.inventories.items.forEach(
                (item: Inventory) => {
                  const inventoryCacheId = cache.identify({
                    __typename: "Inventory",
                    id: item.id,
                  });
                  if (inventoryCacheId) {
                    cache.modify({
                      id: inventoryCacheId,
                      fields: {
                        isPurchased() {
                          return true;
                        },
                      },
                    });
                  }
                },
              );
            }
          }
          const inventoryCost = getInventoryCost(bundle?.prices as InventoryCost[], spendableType);
          if (inventoryCost?.amount) {
            //update new coinWallet
            cache.modify({
              id: "ROOT_QUERY",
              fields: {
                me(existingMe) {
                  cache.modify({
                    id: existingMe.__ref,
                    fields: {
                      coinWallets(coinWallets) {
                        const spendableGzTokens =
                          User.getSpendableGzTokens(coinWallets, spendableType) ?? 0;
                        return coinWallets.map((wallet: CoinWallet) => {
                          if (wallet.itemType === spendableType) {
                            return {
                              ...wallet,
                              balance: spendableGzTokens - inventoryCost.amount,
                            };
                          }
                          return wallet;
                        });
                      },
                    },
                  });
                  return existingMe;
                },
              },
            });
          }
        },
      });
      if (result.errors instanceof ApolloError) {
        const specificError = result.errors.message;
        dispatch(removePopupQueue({ type: PopupType.PurchaseAvatar }));
        if (specificError.includes("User is unable to afford")) {
          dispatch(addPopupQueue({ type: PopupType.PurchaseResult }));
        } else dispatch(showSnackbar(specificError));
      } else {
        dispatch(
          updateOpeningInventory({
            openingInventory: {
              ...bundle,
              isPurchased: true,
            },
          }),
        );
        dispatch(removePopupQueue({ type: PopupType.PurchaseAvatar }));
      }
    } catch (error) {
      dispatch(removePopupQueue({ type: PopupType.PurchaseAvatar }));
      console.error(error);
    }
    if (showPopup) {
      dispatch(addPopupQueue({ type: PopupType.PurchaseResult }));
    }
  };

  return purchaseInventory;
};

// equip avatar function
export const useApplyInventory = (openingInventory?: Inventory) => {
  const [applyInventoryAction] = useMutation(applyInventoryGQL);

  const applyInventory = useCallback(() => {
    applyInventoryAction({
      variables: { input: { inventoryId: openingInventory?.id } },
      update(cache) {
        cache.modify({
          id: "ROOT_QUERY",
          fields: {
            me(existingMe) {
              const userId = existingMe.__ref;
              cache.modify({
                id: userId,
                fields: {
                  avatar() {
                    return (openingInventory?.data as InventoryAvatarData).url;
                  },
                },
              });
              return existingMe;
            },
          },
        });
      },
    });
  }, [applyInventoryAction, openingInventory]);

  return applyInventory;
};
